import { Badge, type BadgeTone } from '../primitives/Badge.js';
import { tokens } from '../tokens.js';

export type StatusEntity =
  | 'order'
  | 'product'
  | 'purchaseOrder'
  | 'quote'
  | 'saleQuote'
  | 'demand'
  | 'movement'
  | 'payment'
  | 'document'
  | 'documentKind'
  | 'batch'
  | 'b2bOrder'
  | 'b2bDeliveryNote'
  | 'b2bClientKind'
  | 'locationType'
  | 'leadFlow'
  | 'record';

export type StatusConfig = { label: string; tone: BadgeTone };

export const statusConfig: Record<StatusEntity, Record<string, StatusConfig>> = {
  order: {
    PENDING: { label: 'En attente', tone: 'warning' },
    CONFIRMED: { label: 'Confirmée', tone: 'info' },
    SHIPPED: { label: 'Expédiée', tone: 'info' },
    DELIVERED: { label: 'Livrée', tone: 'success' },
    CANCELLED: { label: 'Annulée', tone: 'danger' },
    RETURNED: { label: 'Retournée', tone: 'warning' },
  },
  product: {
    DRAFT: { label: 'Brouillon', tone: 'neutral' },
    ACTIVE: { label: 'Actif', tone: 'success' },
    ARCHIVED: { label: 'Archivé', tone: 'neutral' },
    INACTIVE: { label: 'Inactif', tone: 'warning' },
    OUT_OF_STOCK: { label: 'Rupture', tone: 'danger' },
    DISCONTINUED: { label: 'Arrêté', tone: 'neutral' },
  },
  purchaseOrder: {
    DRAFT: { label: 'Brouillon', tone: 'neutral' },
    ORDERED: { label: 'Commandée', tone: 'info' },
    PARTIALLY_RECEIVED: { label: 'Part. reçue', tone: 'warning' },
    COMPLETED: { label: 'Terminée', tone: 'success' },
    CANCELLED: { label: 'Annulée', tone: 'danger' },
  },
  quote: {
    DRAFT: { label: 'Brouillon', tone: 'neutral' },
    SENT: { label: 'Envoyé', tone: 'info' },
    RECEIVING: { label: 'Réponses en cours', tone: 'warning' },
    AWARDED: { label: 'Attribué', tone: 'success' },
    CANCELLED: { label: 'Annulé', tone: 'danger' },
    EXPIRED: { label: 'Expiré', tone: 'danger' },
  },
  saleQuote: {
    DRAFT: { label: 'Brouillon', tone: 'neutral' },
    SENT: { label: 'Envoyé', tone: 'info' },
    ACCEPTED: { label: 'Accepté', tone: 'success' },
    CONVERTED: { label: 'Converti', tone: 'success' },
    REJECTED: { label: 'Refusé', tone: 'danger' },
    EXPIRED: { label: 'Expiré', tone: 'danger' },
    CANCELLED: { label: 'Annulé', tone: 'danger' },
  },
  demand: {
    PENDING: { label: 'En attente', tone: 'warning' },
    UNDER_REVIEW: { label: 'En examen', tone: 'info' },
    APPROVED: { label: 'Approuvé', tone: 'success' },
    REJECTED: { label: 'Rejeté', tone: 'danger' },
  },
  movement: {
    SALE: { label: 'Vente', tone: 'danger' },
    PURCHASE: { label: 'Achat', tone: 'success' },
    ADJUSTMENT: { label: 'Ajustement', tone: 'info' },
  },
  payment: {
    UNPAID: { label: 'Non payé', tone: 'warning' },
    PARTIAL: { label: 'Partiel', tone: 'warning' },
    PAID: { label: 'Payé', tone: 'success' },
    REFUNDED: { label: 'Remboursé', tone: 'info' },
    FAILED: { label: 'Échec', tone: 'danger' },
  },
  document: {
    PENDING: { label: 'En attente', tone: 'warning' },
    GENERATED: { label: 'Généré', tone: 'success' },
    FAILED: { label: 'Échec', tone: 'danger' },
  },
  documentKind: {
    INVOICE: { label: 'Facture', tone: 'info' },
    PURCHASE_ORDER_NOTE: { label: 'Note BC', tone: 'info' },
    RECEPTION_NOTE: { label: 'Note réception', tone: 'info' },
    SALE_QUOTE: { label: 'Devis', tone: 'info' },
  },
  batch: {
    ACTIVE: { label: 'Vendable', tone: 'success' },
    NEAR_EXPIRY: { label: 'Péremption proche', tone: 'warning' },
    EXPIRED: { label: 'Expiré', tone: 'danger' },
    RECALLED: { label: 'Rappelé', tone: 'warning' },
  },
  b2bOrder: {
    DRAFT: { label: 'Brouillon', tone: 'neutral' },
    CONFIRMED: { label: 'Confirmée', tone: 'info' },
    DELIVERED: { label: 'Livrée', tone: 'success' },
    CANCELLED: { label: 'Annulée', tone: 'danger' },
  },
  b2bDeliveryNote: {
    DRAFT: { label: 'Brouillon', tone: 'neutral' },
    ISSUED: { label: 'Émis', tone: 'success' },
  },
  b2bClientKind: {
    MANAGED: { label: 'Géré', tone: 'info' },
    EXTERNAL: { label: 'Externe', tone: 'neutral' },
  },
  locationType: {
    WAREHOUSE: { label: 'Entrepôt', tone: 'info' },
    SHOWROOM_OUTLET: { label: 'Showroom', tone: 'success' },
  },
  leadFlow: {
    PUBLISHED: { label: 'Publié', tone: 'success' },
    DRAFT: { label: 'Brouillon', tone: 'neutral' },
  },
  record: {
    DELETED: { label: 'Supprimé', tone: 'neutral' },
    ARCHIVED: { label: 'Archivé', tone: 'neutral' },
  },
};

export function getStatusConfig(entity: StatusEntity, status: string): StatusConfig {
  const s = String(status ?? '').toUpperCase();
  return statusConfig[entity]?.[s] ?? { label: s || '—', tone: 'neutral' };
}

export interface StatusBadgeProps {
  entity: StatusEntity;
  status: string | null | undefined;
  variant?: 'filled' | 'light' | 'outline' | 'dot';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export function StatusBadge({ entity, status, size = 'sm' }: StatusBadgeProps) {
  const { label, tone } = getStatusConfig(entity, status ?? '');
  const fz = size === 'xs' ? tokens.typography.badgeUppercasePx : 11;
  return (
    <Badge
      purpose="status"
      tone={tone}
      style={{
        fontWeight: 700,
        letterSpacing: '0.01em',
        fontSize: fz,
      }}
    >
      {label}
    </Badge>
  );
}
